import type { H3Event } from 'h3'

export interface JpsResponseError extends Error {
  type?: string
  title?: string
  detail?: string
  info?: string[]
  timestamp?: string
}

const baseUrl = () => {
  const config = useRuntimeConfig()
  return import.meta.client ? window.location.origin : config.internalApiBaseUrl
}

const endpoint = (url: string): string => {
  const endpoint = url.startsWith('/') ? baseUrl() + url : url
  return endpoint
}

const fetchErrorMessage = (_endpoint: string, res: Response) => {
  return `FetchError. endpoint: ${_endpoint}, status: ${res?.status}, api-track-id: ${res?.headers?.get('x-track-id')}, stack: ${new Error('fetch-error').stack}.`
}

const createFetchError = (target: any, isCatch: boolean): JpsResponseError => {
  if (isCatch) {
    return target.data as JpsResponseError
  } else {
    return target._data as JpsResponseError
  }
}

const logging = (
  _start: number,
  msg: string,
  _endpoint: string,
  res: Response,
  h3Event?: H3Event,
) => {
  if (import.meta.server) {
    //  性能試験用にAPI呼び出しに対して、ログ出力を追加。
    //  性能試験の際に、ログレベルを、console.debug から console.info に変更して利用する。
    // const requestTime = Date.now() - _start
    // /* eslint-disable-next-line no-console */
    // console.debug(`Api ${msg} Elapsed ${requestTime}ms.`)
    // if (!res.ok) {
    //   //  API通信でエラーの場合、ログを出力
    //   /* eslint-disable-next-line no-console */
    //   console.warn(fetchErrorMessage(_endpoint, res))
    // }
  }
}

export const useCustomFetch = () => {
  const xsrfCookie = useCookie(XSRF_COOKIE_NAME)
  const cookieHeader = useRequestHeaders(['cookie'])
  const { setCookie } = useCustomCookie()

  function prepareHeader(
    isPost = false,
    headers?: Record<string, string>,
    additionals: Record<string, string> = {},
  ) {
    if (!headers) headers = {}
    if (import.meta.client) {
      //クライアント側
      if (isPost) headers['X-XSRF-TOKEN'] = xsrfCookie.value
    } else {
      //サーバ側
      //ページリクエストのCookieをAPI呼び出しに渡す
      if (cookieHeader.cookie) headers['Cookie'] = cookieHeader.cookie
    }
    const header = {
      ...headers,
      ...additionals,
      'X-Requested-With': 'XmlHttpRequest',
    }
    // console.info(
    //   'preapre-header',
    //   import.meta.client ? 'client' : 'server',
    //   headers,
    // )
    return header
  }

  async function fetchGetRes(
    url: string,
    headers?: Record<string, string>,
    h3Event?: H3Event,
    responseType?: string,
  ): Promise<Response> {
    const _endpoint = endpoint(url)
    const _start = Date.now()

    try {
      let options = {
        headers: prepareHeader(false, headers)
      }
      if (responseType) {
        options['responseType'] = responseType
      }
      const res = await $fetch.raw(_endpoint, options)
      if (!res.ok) {
        throw createFetchError(res, false)
      }
      if (!import.meta.client) {
        //サーバサイドの呼び出しでセットされるCookieがあればクライアントに転送
        setCookie([XSRF_COOKIE_NAME, SESSION_COOKIE_NAME], res)
      }
      logging(_start, `fetchGetRes: ${_endpoint}`, _endpoint, res, h3Event)
      return res
    } finally {
      logging(_start, `fetchGetRes: ${_endpoint}`, _endpoint, null, h3Event)
    }
  }

  /**
   * CustomHeaderを付与してfetchのGETを実施する関数
   *
   * 自システムのAPIなどを呼び出す場合はCustomHeaderが必要となるため、自システムへのアクセス時にはこちらを利用すること。
   */
  async function fetchGet<R>(
    url: string,
    headers?: Record<string, string>,
    h3Event?: H3Event,
  ): Promise<R> {
    const _endpoint = endpoint(url)
    const _start = Date.now()
    try {
      const res = await $fetch.raw<R>(_endpoint, {
        headers: prepareHeader(false, headers),
      })
      if (!res.ok) {
        throw createFetchError(res, false)
      }
      if (!import.meta.client) {
        //サーバサイドの呼び出しでセットされるCookieがあればクライアントに転送
        setCookie([XSRF_COOKIE_NAME, SESSION_COOKIE_NAME], res)
      }
      logging(_start, `fetchGet: ${_endpoint}`, _endpoint, res, h3Event)
      return res._data
    } finally {
      logging(_start, `fetchGet: ${_endpoint}`, _endpoint, null, h3Event)
    }
  }

  /**
   * CustomHeaderを付与せずにfetchのGETを実施する関数
   *
   * CustomHeaderを付与しないことによって自システム以外へのアクセスでCORS関連の問題を避けることが出来るため、
   * CustomHeaderを必要としない自システム以外へのアクセス時にはこちらを利用すること。
   */
  async function fetchGetWithoutCustomHeader<R>(
    url: string,
    h3Event?: H3Event,
  ): Promise<R> {
    const _endpoint = endpoint(url)
    const _start = Date.now()
    try {
      const res = await $fetch.raw<R>(_endpoint)
      if (!res.ok) {
        throw createFetchError(res, false)
      }
      if (!import.meta.client) {
        //サーバサイドの呼び出しでセットされるCookieがあればクライアントに転送
        setCookie([XSRF_COOKIE_NAME, SESSION_COOKIE_NAME], res)
      }
      logging(_start, `fetchGetWithoutCustomHeader: ${_endpoint}`, _endpoint, res, h3Event)
      return res._data
    } finally {
      logging(_start, `fetchGetWithoutCustomHeader: ${_endpoint}`, _endpoint, null, h3Event)
    }
  }

  async function fetchPost<T, R>(
    url: string,
    obj?: T,
    headers?: Record<string, string>,
    h3Event?: H3Event,
  ): Promise<R> {
    const _endpoint = endpoint(url)
    const _start = Date.now()
    try {
      const res = await $fetch.raw<R>(_endpoint, {
        method: 'POST',
        headers: prepareHeader(true, headers),
        body: obj,
        credentials: 'same-origin',
      })

      if (!res.ok) {
        throw createFetchError(res, false)
      }
      if (!import.meta.client) {
        //サーバサイドのAPI呼び出しでセットされるCookieがあればクライアントに転送
        setCookie([XSRF_COOKIE_NAME, SESSION_COOKIE_NAME], res)
      }
      logging(_start, `fetchPost: ${_endpoint}`, _endpoint, res, h3Event)
      return res._data
    } catch (e) {
      console.error('fetch-post', e)
      if (e.data && !e.response.ok) {
        throw createFetchError(e, true)
      } else {
        throw e
      }
    } finally {
      logging(_start, `fetchPost: ${_endpoint}`, _endpoint, null, h3Event)
    }
  }

  async function fetchPostForm<R>(
    url: string,
    form: FormData | { [key: string]: any },
    headers?: Record<string, string>,
    h3Event?: H3Event,
  ): Promise<R> {
    const _endpoint = endpoint(url)
    const _start = Date.now()
    try {
      if (!(form instanceof FormData)) {
        let formData = new FormData()
        for (const key in form) {
          formData.append(key, form[key])
        }
        form = formData
      }
      const res = await $fetch.raw<R>(_endpoint, {
        method: 'POST',
        headers: prepareHeader(true, headers),
        body: form,
        credentials: 'same-origin',
      })

      if (!res.ok) {
        throw createFetchError(res, false)
      }
      if (!import.meta.client) {
        //サーバサイドのAPI呼び出しでセットされるCookieがあればクライアントに転送
        setCookie([XSRF_COOKIE_NAME, SESSION_COOKIE_NAME], res)
      }
      logging(_start, `fetchPostForm: ${_endpoint}`, _endpoint, res, h3Event)
      return res._data
    } catch (e) {
      console.error('fetchPostForm', e)
      if (e.data && !e.response.ok) {
        throw createFetchError(e, true)
      } else {
        throw e
      }
    } finally {
      logging(_start, `fetchPostForm: ${_endpoint}`, _endpoint, null, h3Event)
    }
  }

  async function fetchDelete<R>(
    url: string,
    headers?: Record<string, string>,
    h3Event?: H3Event,
  ): Promise<R> {
    const _endpoint = endpoint(url)
    const _start = Date.now()
    try {
      const res = await $fetch.raw<R>(_endpoint, {
        method: 'DELETE',
        headers: prepareHeader(true, headers),
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw createFetchError(res, false)
      }
      if (!import.meta.client) {
        //サーバサイドの呼び出しでセットされるCookieがあればクライアントに転送
        setCookie([XSRF_COOKIE_NAME, SESSION_COOKIE_NAME], res)
      }
      logging(_start, `fetchDelete: ${_endpoint}`, _endpoint, res, h3Event)
      return res._data
    } finally {
      logging(_start, `fetchDelete: ${_endpoint}`, _endpoint, null, h3Event)
    }
  }

  return {
    fetchGetRes,
    fetchGet,
    fetchGetWithoutCustomHeader,
    fetchPost,
    fetchPostForm,
    fetchDelete,
  }
}
